import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { NovaButton } from '@rds-nova/angular-button';
import { NovaIcon } from '@rds-nova/angular-icon';

import { TrackedButtonDirective } from 'src/app/core/analytics/directives/tracked-button.directive';
import { AcknowledgementType } from 'src/app/core/enums/acknowledgement-type.enum';
import {
    LegalAcknowledgementFooterComponent
} from 'src/app/features/check-in/components/legal-acknowledgement-footer/legal-acknowledgement-footer.component';
import {
    LegalAcknowledgementsStore
} from 'src/app/features/check-in/store/legal-acknowledgements.store';
import { Feature } from 'src/app/features/feature-management/enums/feature.enum';
import {
    selectIsFeatureEnabled
} from 'src/app/features/feature-management/store/selectors/feature-flags.selectors';
import {
    selectIsRemoteSigning, selectIsSessionLoading, selectParticipant, selectParticipantFirstName
} from 'src/app/features/session/store/selectors/session.selectors';
import { AppState } from 'src/app/store';

import { InfoBarComponent } from './components/info-bar/info-bar.component';

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  imports: [
    InfoBarComponent,
    LegalAcknowledgementFooterComponent,
    NgTemplateOutlet,
    NovaButton,
    NovaIcon,
    TranslatePipe,
    TrackedButtonDirective,
  ],
  standalone: true,
  providers: [LegalAcknowledgementsStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingPageComponent {
  private router = inject(Router);
  private readonly appStore = inject(Store<AppState>);
  private readonly legalAcknowledgementStore = inject(
    LegalAcknowledgementsStore,
  );

  participant = this.appStore.selectSignal(selectParticipant);
  isSessionLoading = this.appStore.selectSignal(selectIsSessionLoading);
  firstName = this.appStore.selectSignal(selectParticipantFirstName);
  isRemoteSigning = this.appStore.selectSignal(selectIsRemoteSigning);
  isBypassIdCaptureEnabled = this.appStore.selectSignal(
    selectIsFeatureEnabled(Feature.BypassIdCapture),
  );
  isLoaded = computed(
    () => !this.isSessionLoading() && this.participant() !== null,
  );
  isIdCaptureRequired = computed(
    () => this.isRemoteSigning() && !this.isBypassIdCaptureEnabled(),
  );

  legalAcknowledgements = [
    AcknowledgementType.PrivacyPolicy,
    AcknowledgementType.TermsOfUse,
    AcknowledgementType.AiDisclaimer
  ];

  async navigateToNextStep() {
    const route = this.isIdCaptureRequired()
      ? '/id-verification'
      : '/signature-and-initials';
    try {
      await this.legalAcknowledgementStore.acceptAcknowledgements(
        this.legalAcknowledgements,
      );

      this.router.navigate([route], {
        queryParamsHandling: 'preserve',
      });
    } catch {
      // Display alert or something here
      console.info("Failed to acknowledge, TOU, PP and AI Disclaimer");
    }
  }
}
